// src/constants/screenerConfig.js
// Extracted from ScreenerPage.jsx (SCREENER-FRAG) — content unchanged, location only.
// Static config: scan definitions, sectors, sort options, technical columns,
// D3 strategy map, guest limit, style tokens.
//
// API_URL added here (was a module-level const in ScreenerPage.jsx) so every
// fragmented file — SavedScansPanel, useScreenerData, useScreenerSignal —
// sources the same value instead of each redeclaring `process.env...` and
// risking drift if the API base ever changes.
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── D3 scan definitions ───────────────────────────────────────
// Each scan maps to a D3 strategy — this IS the Tier 3 signal value.
// When member clicks "Volume Breakouts", D3 knows they're watching Strategy A.
export const SCANS = [
  { key: 'top_bullish',     label: '🔥 Top BULLISH',        d3: 'B_MOMENTUM',    desc: 'Highest scored BULLISH signals' },
  { key: 'volume_breakout', label: '⚡ Volume Breakouts',   d3: 'A_PUMP',        desc: 'Volume surging above average' },
  { key: 'momentum_surge',  label: '📈 Momentum Surge',     d3: 'B_MOMENTUM',    desc: 'Strong upward momentum today' },
  { key: 'oversold_bounce', label: '🔄 Oversold Bounce',    d3: 'C_MEAN_REVERT', desc: 'BEARISH signal — potential reversal' },
  { key: 'high_52w',        label: '🎯 52W High Breakout',  d3: 'H_CONSOLIDATION', desc: 'Price near 52-week high' },
  { key: 'value_dip',       label: '💎 Value Dip',          d3: 'C_MEAN_REVERT', desc: 'Low score — potential value entry' },
  { key: 'earnings_week',   label: '📅 Earnings This Week', d3: 'E_EARNINGS',    desc: 'Upcoming earnings catalysts' },
];

export const SECTORS = ['ALL', 'Technology', 'Finance', 'Healthcare', 'Consumer', 'Energy', 'Industrial', 'ETF', 'Other'];

export const SORT_OPTIONS = [
  { key: 'score',  label: 'Signal Score' },
  { key: 'change', label: 'Price Change %' },
  { key: 'volume', label: 'Volume' },
  { key: 'price',  label: 'Price' },
];

// S27J: Technical column definitions
// RSI deferred to S27K (requires TIME_SERIES_DAILY)
export const TECH_COLUMNS = [
  { key: '52w',    label: '52W Pos%', title: '52-Week Position — where price sits in its annual range (100=near 52W high, 0=near 52W low)' },
  { key: 'volrat', label: 'Vol Ratio', title: 'Volume vs average (EMA). >1.5 = elevated, <0.5 = quiet. Builds precision over ~10 refresh cycles.' },
];

export const SCAN_D3_MAP = {
  top_bullish:     'B_MOMENTUM',
  volume_breakout: 'A_PUMP',
  oversold_bounce: 'C_MEAN_REVERT',
  momentum_surge:  'B_MOMENTUM',
  high_52w:        'H_CONSOLIDATION',
  value_dip:       'C_MEAN_REVERT',
  earnings_week:   'E_EARNINGS',
};

// ── Proven Screening Methods — added after owner-directed competitive research ──
// Real, documented methodologies (Greenblatt's Magic Formula, Piotroski's F-Score, O'Neil's
// CANSLIM, Minervini's Trend Template) — not invented. Honesty about current data gaps matters
// here as much as it does in screenerFieldRegistry.js: most of these need fundamentals data
// (P/E, ROIC, margins — XBRL-EARNINGS-INTEL, not built) or technical history (SMA20/50/200 —
// Stooq, not built). status:'partial' means SOME criteria are computable today with existing
// fields (price/volume/52W range); status:'pending' means none are yet — selecting one shows a
// clear "criteria pending" state rather than silently returning unfiltered/wrong results.
export const PROVEN_SCREENS = [
  {
    key: 'minervini_trend', label: 'Minervini Trend Template',
    source: 'Mark Minervini, "Trade Like a Stock Market Wizard"',
    status: 'partial',
    criteria: [
      'Price above 52W low by 25%+ (computable now — pos52w)',
      'Price within 25% of 52W high (computable now — pos52w)',
      'Price > 150-day MA > 200-day MA (PENDING — needs SMA150/200, Stooq)',
      '200-day MA trending up 1+ month (PENDING — needs SMA200 history, Stooq)',
      'Relative strength rank >= 70 (PENDING — needs peer-relative calc)',
    ],
  },
  {
    key: 'canslim', label: 'CANSLIM',
    source: 'William O\'Neil, "How to Make Money in Stocks"',
    status: 'pending',
    criteria: [
      'Current + annual EPS growth (PENDING — fundamentals, XBRL-EARNINGS-INTEL)',
      'New highs / new products (PENDING — needs 52W high + news correlation)',
      'Supply/demand — volume confirmation (computable now — volume/avgVolume)',
      'Leader vs laggard — relative strength (PENDING)',
      'Institutional sponsorship (PENDING — OCI-5G 13F data)',
    ],
  },
  {
    key: 'magic_formula', label: 'Magic Formula',
    source: 'Joel Greenblatt, "The Little Book That Beats the Market"',
    status: 'pending',
    criteria: [
      'High earnings yield (EBIT/EV) (PENDING — fundamentals)',
      'High return on invested capital (PENDING — fundamentals)',
    ],
  },
  {
    key: 'piotroski_fscore', label: 'Piotroski F-Score',
    source: 'Joseph Piotroski (2000), 9-signal fundamental checklist',
    status: 'pending',
    criteria: [
      'ROA > 0, CFO > 0, rising ROA, CFO > NI (accruals) (PENDING — fundamentals)',
      'Falling leverage, rising current ratio, no new shares issued (PENDING)',
      'Rising gross margin, rising asset turnover (PENDING)',
    ],
  },
  {
    key: 'golden_cross', label: 'Golden Cross',
    source: 'Classic technical pattern — 50-day MA crosses above 200-day MA',
    status: 'pending',
    criteria: ['50-day MA crosses above 200-day MA (PENDING — SMA50/200, Stooq)'],
  },
  {
    key: 'volume_momentum', label: 'Volume Momentum (Zweig-style)',
    source: 'Martin Zweig — volume-confirmed breakout',
    status: 'partial',
    criteria: [
      'Volume > 1.5x average (computable now — avgVolume ratio)',
      'Price change positive same day (computable now — changePercent)',
      'Breadth/advance-decline confirmation (PENDING — market-wide calc)',
    ],
  },
  {
    key: 'quality_discount', label: 'Quality at a Discount',
    source: 'Compounding Quality / quality-value hybrid approach',
    status: 'pending',
    criteria: ['High quality (margins, ROE) + low valuation (P/E, P/B) (PENDING — fundamentals)'],
  },
  {
    key: 'dividend_growth', label: 'Dividend Growth',
    source: 'Dividend Aristocrats-style consistent-growth approach',
    status: 'pending',
    criteria: ['Consecutive years of dividend growth (PENDING — fundamentals)'],
  },
  {
    key: 'tiny_titans', label: 'Tiny Titans',
    source: 'Stockopedia — small-cap value + quality screen',
    status: 'pending',
    criteria: ['Small market cap + low valuation + quality filters (PENDING — fundamentals + market cap)'],
  },
  {
    key: 'relative_strength_leaders', label: 'Relative Strength Leaders',
    source: 'Common institutional momentum approach (top-decile RS)',
    status: 'pending',
    criteria: ['Top 10% relative price performance vs universe (PENDING — needs peer-relative calc across full universe, not just 20 tracked tickers)'],
  },
];

export const GUEST_LIMIT = 10;

export const S = {
  bg:      '#0f0f23',
  border:  '#1e1e3a',
  surface: '#1a1a2e',
  border2: '#2a2a4a',
  text:    '#e2e8f0',
  muted:   '#8b8ba7',
  subtle:  '#475569',
  green:   '#10b981',
  red:     '#ef4444',
  purple:  '#6366f1',
  amber:   '#f59e0b',
  blue:    '#3b82f6',
};
